import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthHeader, defaultAuthMenuItems } from '../auth/AuthHeader';
import { apiClient } from '../../api/httpClient';
import { zynity_api } from '../../config/external-routes';
import { getPasswordError } from './registrationUtils';
import '../auth/LoginPage.css';
import './PeopleRegistration.css';

const SUCCESS_MESSAGE =
  'Your registration was accepted. You can now login with the username and password you just entered.';

export const PeopleMemberSignUpPage: React.FC = () => {
  const location = useLocation();
  const [formValues, setFormValues] = useState({
    FirstName: '',
    LastName: '',
    EmailAddress: '',
    KnownAs: '',
    newPassword: '',
    newPasswordRepeat: '',
  });
  const [orgName, setOrgName] = useState('your organization');
  const [statusMessage, setStatusMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const id = queryParams.get('id') || '';
  const oid = queryParams.get('oid') || '';
  const code = queryParams.get('code') || '';
  const rootUrl = process.env.REACT_APP_ZYNITY_YII_URL || '';

  useEffect(() => {
    let isMounted = true;
    if (!id || !oid || !code) {
      return undefined;
    }

    apiClient
      .post(zynity_api.people_member_signup, { id, oid, code })
      .then((response) => {
        if (!isMounted) return;
        const data = response.data || {};
        const resolvedOrg =
          data.OrgName ||
          data.orgName ||
          data.organization ||
          data.OrganizationName ||
          '';
        const resolvedEmail =
          data.EmailAddress ||
          data.emailAddress ||
          data.email ||
          '';
        if (resolvedOrg) {
          setOrgName(resolvedOrg);
        }
        if (resolvedEmail) {
          setFormValues((current) => ({ ...current, EmailAddress: resolvedEmail }));
        }
      })
      .catch(() => {
        // Prefill is optional; ignore errors.
      });

    return () => {
      isMounted = false;
    };
  }, [id, oid, code]);

  const handleChange =
    (field: keyof typeof formValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((current) => ({ ...current, [field]: event.target.value }));
    };

  const validate = () => {
    if (!id || !oid || !code) {
      return 'Missing invitation details. Please use the link from your email.';
    }
    if (!formValues.FirstName.trim() || !formValues.LastName.trim() || !formValues.KnownAs.trim()) {
      return 'Please complete all required fields.';
    }
    const passwordError = getPasswordError(formValues.newPassword, formValues.newPasswordRepeat);
    if (passwordError) {
      return passwordError;
    }
    return '';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    const validationError = validate();
    if (validationError) {
      window.alert(validationError);
      return;
    }

    setIsSubmitting(true);
    setStatusMessage('');
    try {
      const payload = {
        id,
        oid,
        code,
        FirstName: formValues.FirstName.trim(),
        LastName: formValues.LastName.trim(),
        EmailAddress: formValues.EmailAddress.trim(),
        KnownAs: formValues.KnownAs.trim(),
        newPassword: formValues.newPassword,
        newPasswordRepeat: formValues.newPasswordRepeat,
      };
      await apiClient.post(zynity_api.people_member_signup, payload);
      const loginUrl = `/site/login?msg=${encodeURIComponent(SUCCESS_MESSAGE)}`;
      window.location.assign(loginUrl);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to complete registration. Please try again.';
      setStatusMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-page">
      <AuthHeader menuItems={defaultAuthMenuItems} />

      <main className="registration-content">
        <h1 className="registration-title">New Member Sign Up</h1>
        <div className="registration-card">
          {statusMessage && (
            <p className="registration-message error">{statusMessage}</p>
          )}

          <div className="registration-intro">
            <p>You have been invited to join a team on Zynity.com</p>
            <p>Complete the form below and submit (Save) to complete your registration.</p>
            <p>You will then be able to log-in and visit {orgName} on your Community page.</p>
            <p>Please contact the person listed in the email you received if you have any questions.</p>
          </div>

          <form className="registration-form" onSubmit={handleSubmit}>
            <div className="registration-grid">
              <div className="registration-field">
                <label htmlFor="FirstName">First Name *</label>
                <input
                  id="FirstName"
                  name="FirstName"
                  value={formValues.FirstName}
                  onChange={handleChange('FirstName')}
                  required
                />
              </div>
              <div className="registration-field">
                <label htmlFor="LastName">Last Name *</label>
                <input
                  id="LastName"
                  name="LastName"
                  value={formValues.LastName}
                  onChange={handleChange('LastName')}
                  required
                />
              </div>
              <div className="registration-field">
                <label htmlFor="EmailAddress">Email Address *</label>
                <input
                  id="EmailAddress"
                  name="EmailAddress"
                  value={formValues.EmailAddress}
                  readOnly
                />
                <span className="registration-helper">Also your username to login.</span>
              </div>
              <div className="registration-field">
                <label htmlFor="KnownAs">Known As *</label>
                <input
                  id="KnownAs"
                  name="KnownAs"
                  value={formValues.KnownAs}
                  onChange={handleChange('KnownAs')}
                  required
                />
                <span className="registration-helper">Your name or nickname.</span>
              </div>
              <div className="registration-field">
                <label htmlFor="newPassword">Password *</label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  value={formValues.newPassword}
                  onChange={handleChange('newPassword')}
                  required
                />
                <span className="registration-helper">
                  Password can be a combination of letters, numbers and these symbols: ! @ # $ % ^ *
                </span>
              </div>
              <div className="registration-field">
                <label htmlFor="newPasswordRepeat">Confirm Password *</label>
                <input
                  id="newPasswordRepeat"
                  name="newPasswordRepeat"
                  type="password"
                  value={formValues.newPasswordRepeat}
                  onChange={handleChange('newPasswordRepeat')}
                  required
                />
              </div>
            </div>
            <button type="submit" className="registration-submit" disabled={isSubmitting}>
              Save
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};
